import React, { useEffect, useState, useMemo } from "react";
import { Container, Row, Col, Card, CardBody, Table, Button, Modal, ModalHeader, ModalBody, Badge, Input, Label, FormGroup } from "reactstrap";
import Select from "react-select";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { getSeparationList, saveSeparation, getSeparationById } from "../../components/Api/GmsEmployeeSeparation";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { getEmployeesWithDetails } from "../../components/Api/GmsEmployee";
import Swal from "sweetalert2";
import { Eye, CheckCircle, Search, Filter, XCircle, User, DollarSign, ShieldCheck } from "lucide-react";
import API_URL from "../../config";
import { hasPagePermissionWithAdmin, hasApprovePermissionWithAdmin, isUserAdmin } from "../../components/Permission/PermissionService";

const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const normalizeId = (value) => {
        if (!value || value === 'null') return null;
        return parseInt(value, 10) || null;
    };
    return {
        userId: authUser.userId || user.user_Id || 1,
        businessId: normalizeId(authUser.companyId || user.user_Company_Id),
        branchId: normalizeId(authUser.branchId || user.user_Branch_Id),
    };
};

const EmployeeSeparationListPage = () => {
    const session = useMemo(() => getUserSession(), []);
    const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
    const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsEmployeeSeparationList');
    const canApprove = hasApprovePermissionWithAdmin(permissions, '/GmsEmployeeSeparationList');
    const isAdmin = isUserAdmin();

    const [loading, setLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [separations, setSeparations] = useState([]);
    const [businesses, setBusinesses] = useState([]);
    const [branches, setBranches] = useState([]);
    
    // Filters logic
    const [filter, setFilter] = useState({
        businessId: session.businessId,
        branchId: session.branchId,
        employeeName: ""
    });

    const [viewModal, setViewModal] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    useEffect(() => {
        if (!hasPermission) {
            window.location.href = "/Dashboard";
            return;
        }

        const fetchInitData = async () => {
            try {
                const [bizData, branchData] = await Promise.all([
                    getBusinesses({ business_Active: true }),
                    getBranches({ branch_Active: true })
                ]);
                setBusinesses(bizData || []);
                setBranches(branchData || []);
            } catch (error) {
                console.error("Error fetching init data", error);
            }
        };
        fetchInitData();
        loadSeparations();
    }, [hasPermission]);

    const loadSeparations = async (currentFilter = filter) => {
        setLoading(true);
        try {
            const data = await getSeparationList({
                Business_Id: currentFilter.businessId,
                Branch_Id: currentFilter.branchId,
                EmployeeName: currentFilter.employeeName
            });
            setSeparations(data || []);
        } catch (error) {
            console.error("Error loading separations", error);
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (separation) => {
        const confirm = await Swal.fire({
            title: "Are you sure?",
            text: `Approved separation for ${separation.employeeName}. This will also set their status to Off-Work.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#34c38f",
            cancelButtonColor: "#f46a6a",
            confirmButtonText: "Yes, Approve!"
        });

        if (confirm.isConfirmed) {
            try {
                const result = await saveSeparation({ separationID: separation.separationID }, "APPROVE");
                if (result.id) {
                    Swal.fire("Approved!", result.msg, "success");
                    loadSeparations();
                }
            } catch (error) {
                Swal.fire("Error", error.msg || "Failed to approve", "error");
            }
        }
    };

    const handleViewEmployee = async (sep) => {
        try {
            // Fetch the specific separation record to get the tenure breakdown
            const sepData = await getSeparationById(sep.separationID);
            
            // Reusing getEmployeesWithDetails from GmsEmployee API to get full profile info
            const empData = await getEmployeesWithDetails({ Employee_Id: sep.employeeID });
            
            if (empData && empData.length > 0) {
                // Merge profile data with separation/tenure data
                setSelectedEmployee({ ...empData[0], ...sepData });
                setViewModal(true);
            }
        } catch (error) {
            Swal.fire("Error", "Could not fetch details", "error");
        }
    };

    const filteredBranches = useMemo(() => {
        if (!filter.businessId) return branches;
        return branches.filter(b => b.branch_Buisness_Id === filter.businessId);
    }, [branches, filter.businessId]);

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        const d = String(date.getDate()).padStart(2, '0');
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const y = date.getFullYear();
        return `${d}/${m}/${y}`;
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Human Resources" breadcrumbItem="Separation List" />

                    <div className="d-flex justify-content-end mb-3">
                        <Button
                            color="primary"
                            className="bg-primary-subtle text-primary border-primary-subtle"
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            <Filter size={16} className="me-2" />
                            {showFilters ? "Hide Filters" : "Show Filters"}
                        </Button>
                    </div>

                    <Row>
                        <Col lg={12}>
                            {showFilters && (
                                <Card className="border shadow-sm mb-4">
                                    <CardBody>
                                        <h5 className="mb-3 d-flex align-items-center">
                                            <Filter size={18} className="me-2 text-primary" /> Filter Options
                                        </h5>
                                        <Row className="align-items-end">
                                            <Col md={3}>
                                                <FormGroup className="mb-0">
                                                    <Label className="small fw-bold">Company</Label>
                                                    <Select
                                                        options={businesses.map(b => ({ value: String(b.business_Id), label: String(b.business_NameEn) }))}
                                                        value={businesses.filter(b => b.business_Id === filter.businessId).map(b => ({ value: String(b.business_Id), label: String(b.business_NameEn) }))}
                                                        onChange={(opt) => setFilter({ ...filter, businessId: opt ? parseInt(opt.value) : null, branchId: null })}
                                                        isDisabled={!isAdmin && !!session.businessId}
                                                        placeholder="Select Company"
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col md={3}>
                                                <FormGroup className="mb-0">
                                                    <Label className="small fw-bold">Branch</Label>
                                                    <Select
                                                        options={filteredBranches.map(b => ({ value: String(b.branch_Id), label: String(b.branch_NameEn) }))}
                                                        value={filteredBranches.filter(b => b.branch_Id === filter.branchId).map(b => ({ value: String(b.branch_Id), label: String(b.branch_NameEn) }))}
                                                        onChange={(opt) => setFilter({ ...filter, branchId: opt ? parseInt(opt.value) : null })}
                                                        isDisabled={!isAdmin && !!session.branchId}
                                                        placeholder="Select Branch"
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col md={3}>
                                                <FormGroup className="mb-0">
                                                    <Label className="small fw-bold">Employee Name</Label>
                                                    <Input 
                                                        type="text" 
                                                        placeholder="Search name..."
                                                        value={filter.employeeName}
                                                        onChange={(e) => setFilter({...filter, employeeName: e.target.value})}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col md={3}>
                                                <div className="d-flex gap-2">
                                                    <Button color="primary" onClick={() => loadSeparations()} className="flex-fill">
                                                        <Search size={16} className="me-1" /> Search
                                                    </Button>
                                                    <Button color="light" onClick={() => {
                                                        const newFilter = { businessId: session.businessId, branchId: session.branchId, employeeName: "" };
                                                        setFilter(newFilter);
                                                        loadSeparations(newFilter);
                                                    }}>
                                                        Clear
                                                    </Button>
                                                </div>
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Card>
                            )}

                            <Card>
                                <CardBody>
                                    <div className="table-responsive">
                                        <Table className="table-centered table-nowrap mb-0 table-hover">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>S.No</th>
                                                    <th>Employee Name</th>
                                                    <th>Reason Type</th>
                                                    <th>Date</th>
                                                    <th>Comment</th>
                                                    <th>Status</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading ? (
                                                    <tr><td colSpan="7" className="text-center p-4">Loading data...</td></tr>
                                                ) : separations.length === 0 ? (
                                                    <tr><td colSpan="7" className="text-center p-4">No records found</td></tr>
                                                ) : separations.map((sep, idx) => (
                                                    <tr key={sep.separationID}>
                                                        <td>{idx + 1}</td>
                                                        <td>
                                                            <div className="fw-bold">{sep.employeeName}</div>
                                                            <div className="text-muted small">{sep.emp_No}</div>
                                                        </td>
                                                        <td><Badge color="info" className="p-2 text-dark bg-info-subtle border-info">{sep.separationReason || "Select Reason"}</Badge></td>
                                                        <td>{formatDate(sep.separationDate)}</td>
                                                        <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            {sep.comment || "-"}
                                                        </td>
                                                        <td>
                                                            {sep.approvedDate ? (
                                                                <div>
                                                                    <Badge color="success" className="p-2 mb-1 text-dark bg-success-subtle border-success"><CheckCircle size={12} className="me-1"/> Approved</Badge>
                                                                    {/* <div className="text-dark small"><strong>{sep.approvedByName || "Admin"}</strong></div> */}
                                                                    <div className="text-muted" style={{fontSize: '10px'}}>{formatDate(sep.approvedDate)}</div>
                                                                </div>
                                                            ) : (
                                                                <Badge color="warning" className="p-2 text-dark bg-warning-subtle border-warning">Pending</Badge>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <div className="d-flex gap-2">
                                                                <Button color="light" size="sm" onClick={() => handleViewEmployee(sep)} title="View Details">
                                                                    <Eye size={16} className="text-primary" />
                                                                </Button>
                                                                {!sep.approvedBy && canApprove && (
                                                                    <Button color="success" size="sm" onClick={() => handleApprove(sep)} title="Approve Separation">
                                                                        <CheckCircle size={16} />
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>

             {/* Employee Details Modal */}
             <Modal isOpen={viewModal} toggle={() => setViewModal(false)} size="lg">
                 <ModalHeader toggle={() => setViewModal(false)}>Employee Details</ModalHeader>
                 <ModalBody>
                     {selectedEmployee && (
                         <Row>
                             <Col md={12}>
                                 <div className="text-center mb-4">
                                     {selectedEmployee.employee_ImagePath ? (
                                         <img src={`${API_URL}${selectedEmployee.employee_ImagePath}`} alt="" className="rounded-circle img-thumbnail avatar-xl" />
                                     ) : (
                                         <div className="avatar-xl mx-auto rounded-circle bg-light d-flex align-items-center justify-content-center">
                                             <User size={48} className="text-primary" />
                                         </div>
                                     )}
                                     <h4 className="mt-3 mb-1">{selectedEmployee.employee_NameEn}</h4>
                                     <p className="text-muted">{selectedEmployee.emp_No || selectedEmployee.employee_Code}</p>
                                 </div>
                             </Col>
                             <Col md={6}>
                                 <Table bordered size="sm">
                                     <tbody>
                                         <tr><td><strong>Company</strong></td><td>{selectedEmployee.business_name || "-"}</td></tr>
                                         <tr><td><strong>Branch</strong></td><td>{selectedEmployee.branch_name || "-"}</td></tr>
                                         <tr><td><strong>Department</strong></td><td>{selectedEmployee.department_name || "-"}</td></tr>
                                         <tr><td><strong>Designation</strong></td><td>{selectedEmployee.designation_name || "-"}</td></tr>
                                         <tr><td><strong>Country</strong></td><td>{selectedEmployee.country_name || "-"}</td></tr>
                                     </tbody>
                                 </Table>
                             </Col>
                             <Col md={6}>
                                 <Table bordered size="sm">
                                     <tbody>
                                         <tr><td><strong>Joining Date</strong></td><td>{formatDate(selectedEmployee.employee_JoinDate)}</td></tr>
                                         <tr><td><strong>Gender</strong></td><td>{selectedEmployee.gender_name || "-"}</td></tr>
                                         <tr><td><strong>Contact</strong></td><td>{selectedEmployee.employee_ContactNumber1 || "-"}</td></tr>
                                         <tr><td><strong>Address</strong></td><td>{`${selectedEmployee.employee_Address1 || ''} ${selectedEmployee.employee_Address2 || ''}`}</td></tr>
                                     </tbody>
                                 </Table>
                             </Col>
                             <Col md={12}>
                                 <h6 className="mt-3 text-primary border-bottom pb-1"><DollarSign size={14} className="me-1" /> Salary Breakdown</h6>
                                 <Row>
                                     <Col md={3}><div className="small text-muted">Basic</div><div className="fw-bold">{selectedEmployee.employee_BS?.toLocaleString()}</div></Col>
                                     <Col md={3}><div className="small text-muted">HRA</div><div className="fw-bold">{selectedEmployee.employee_HRA?.toLocaleString()}</div></Col>
                                     <Col md={3}><div className="small text-muted">Transport</div><div className="fw-bold">{selectedEmployee.employee_TA?.toLocaleString()}</div></Col>
                                     <Col md={3}><div className="small text-muted">Food</div><div className="fw-bold">{selectedEmployee.employee_FoodAllowance?.toLocaleString()}</div></Col>
                                     <Col md={3} className="mt-2"><div className="small text-muted">Communication</div><div className="fw-bold">{selectedEmployee.employee_CommunicationAllowance?.toLocaleString()}</div></Col>
                                     <Col md={3} className="mt-2"><div className="small text-muted">Utility</div><div className="fw-bold">{selectedEmployee.employee_UtilityAllowance?.toLocaleString()}</div></Col>
                                     <Col md={3} className="mt-2"><div className="small text-muted">Other</div><div className="fw-bold">{selectedEmployee.employee_OA?.toLocaleString()}</div></Col>
                                     <Col md={3} className="mt-2"><div className="small text-muted">WPS Salary</div><div className="fw-bold">{selectedEmployee.wpsSalary?.toLocaleString()}</div></Col>
                                     <Col md={12} className="mt-2"><div className="small text-muted">Gross Salary</div><div className="fw-bold text-success font-size-14">{selectedEmployee.employee_GS?.toLocaleString()}</div></Col>
                                     
                                     {selectedEmployee.gratuity_Amt > 0 && (
                                         <Col md={12} className="mt-4">
                                             <div className="p-3 rounded border border-success-subtle bg-success-subtle bg-opacity-10 shadow-sm">
                                                 <h6 className="text-success mb-3 d-flex align-items-center fw-bold">
                                                     <ShieldCheck size={18} className="me-2" /> Gratuity Calculation Breakdown
                                                 </h6>
                                                 <Row className="g-3">
                                                     <Col xs={4}>
                                                         <div className="text-muted small">Service Period</div>
                                                         <div className="fw-bold">{selectedEmployee.years}y {selectedEmployee.months}m {selectedEmployee.days}d</div>
                                                     </Col>
                                                     <Col xs={4}>
                                                         <div className="text-muted small">Unpaid Leaves</div>
                                                         <div className="fw-bold text-danger">{selectedEmployee.unpaidLeaveDays} d</div>
                                                     </Col>
                                                     <Col xs={4}>
                                                         <div className="text-muted small">Basis Basic</div>
                                                         <div className="fw-bold">{selectedEmployee.basicSalary?.toLocaleString()}</div>
                                                     </Col>
                                                     <Col xs={12}>
                                                         <div className="mt-2 pt-2 border-top d-flex justify-content-between align-items-center">
                                                             <div className="fw-bold text-success text-uppercase small">Payable Gratuity Amount</div>
                                                             <div className="h4 mb-0 text-success fw-bold">
                                                                 {selectedEmployee.gratuity_Amt?.toLocaleString(undefined, { minimumFractionDigits: 3 })}
                                                             </div>
                                                         </div>
                                                     </Col>
                                                 </Row>
                                             </div>
                                         </Col>
                                     )}
                                 </Row>
                             </Col>
                             <Col md={12}>
                                 <h6 className="mt-3 text-primary border-bottom pb-1">Document Information</h6>
                                 <Table bordered size="sm">
                                     <thead>
                                         <tr className="table-light">
                                             <th>Document</th>
                                             <th>Number</th>
                                             <th>Expiry Date</th>
                                         </tr>
                                     </thead>
                                     <tbody>
                                         <tr>
                                             <td>Passport</td>
                                             <td>{selectedEmployee.employee_PassportNumber || "N/A"}</td>
                                             <td>{formatDate(selectedEmployee.employee_Passport_ExpDate)}</td>
                                         </tr>
                                         <tr>
                                             <td>National ID</td>
                                             <td>{selectedEmployee.employee_NatID || "N/A"}</td>
                                             <td>{formatDate(selectedEmployee.employee_NatID_ExpDate)}</td>
                                         </tr>
                                         <tr>
                                             <td>Visa</td>
                                             <td>{selectedEmployee.employee_VisaNumber || "N/A"}</td>
                                             <td>{formatDate(selectedEmployee.employee_Visa_ExpDate)}</td>
                                         </tr>
                                         <tr>
                                             <td>Labour Card</td>
                                             <td>{selectedEmployee.employee_LabourCardNumber || "N/A"}</td>
                                             <td>{formatDate(selectedEmployee.employee_LabourCardExpiry)}</td>
                                         </tr>
                                     </tbody>
                                 </Table>
                             </Col>
                         </Row>
                     )}
                 </ModalBody>
             </Modal>
        </React.Fragment>
    );
};

export default EmployeeSeparationListPage;
